// src/pages/customer/CustomerAssetPage.js
// 后台 · 客户中心 · 资产管理（doc01 3.8，缺陷 122/123）
// 结构：在用资产 / 报废资产 两个 tab；筛选移入各子 tab、置于表格上方。
//  在用：5 项模糊（资产编号/名称/关联站点/工控机编号/厂商）+ 2 下拉（储能柜类型/是否配置预测值，默认全部）+ 查询·重置；表上方「新增资产」。
//    列：资产编号｜名称｜是否配置预测值｜关联站点｜储能柜类型｜工控机编号｜厂商｜型号｜出厂日期｜功率｜容量｜操作人｜操作（编辑｜管理｜预测值配置｜删除｜报废）。
//    勾选列表前复选框 → 左下角展示已选个数，可批量配置预测值 / 取消，支持翻页勾选。
//  报废：6 项模糊（资产编号/名称/关联站点/工控机编号/厂商/操作人）+ 查询·重置；列含最近关联站点/报废日期/资产去向；操作仅「删除」。
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Button, Input, Select, Modal, Space, Popconfirm, Tag, DatePicker, Upload, message } from 'antd';
import ScrollTabs from '../../components/ScrollTabs';
import { SearchOutlined, PlusOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import PageSkeleton from '../../components/PageSkeleton';
import { findMenu, pick } from '../../config/menuConfig';
import { ASSETS, SCRAPPED_ASSETS, STATIONS, H_CUSTOMERS } from '../../config/stationData';

const labelStyle = { color: 'rgba(255,255,255,0.55)', fontSize: 12 };
const nowStr = () => new Date().toISOString().slice(0, 16).replace('T', ' ');
const currentUser = () => { try { return JSON.parse(localStorage.getItem('currentUser') || '{}').name || '周翔宇'; } catch (e) { return '周翔宇'; } };

const CABINET_OPTIONS = ['储能一体柜', '电池柜', '无'].map((v) => ({ value: v, label: v }));
const PREDICT_OPTIONS = ['已配置', '未配置'].map((v) => ({ value: v, label: v }));
const STATION_OPTIONS = STATIONS.map((s) => ({ value: s.name, label: s.name }));
const CUSTOMER_OPTIONS = H_CUSTOMERS.map((c) => ({ value: c.short, label: c.short }));
const DEVICE_OPTIONS = ['PCS-01', '电池簇-01', 'PV-01', '充电桩-01', '空调-01', '电表-01'].map((v) => ({ value: v, label: v }));

// doc01 3.8 新增资产：「*」必填＝资产编号、名称、厂商、型号、出厂日期、功率、容量；工控机编号非必填
const EMPTY_ASSET = { code: '', name: '', vendor: '', model: '', factory: null, power: '', capacity: '', ipc: '' };
const EMPTY_IN_FILTER = { code: '', name: '', station: '', ipc: '', vendor: '', cabinet: undefined, predict: undefined };
const EMPTY_SC_FILTER = { code: '', name: '', station: '', ipc: '', vendor: '', operator: '' };

const CustomerAssetPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const meta = findMenu('/app/customer/asset');

  const [inuse, setInuse] = useState(() => ASSETS.map((a) => ({ ...a })));
  const [scrapped, setScrapped] = useState(() => SCRAPPED_ASSETS.map((a) => ({ ...a })));
  const [tab, setTab] = useState('inuse');
  const [selected, setSelected] = useState([]);

  // 在用 tab 筛选（置于子 tab 内、表格上方）
  const [inF, setInF] = useState({ ...EMPTY_IN_FILTER });
  const [inApplied, setInApplied] = useState({ ...EMPTY_IN_FILTER });
  // 报废 tab 筛选
  const [scF, setScF] = useState({ ...EMPTY_SC_FILTER });
  const [scApplied, setScApplied] = useState({ ...EMPTY_SC_FILTER });

  const [assetModal, setAssetModal] = useState({ open: false, editing: null, form: EMPTY_ASSET });
  const [manageModal, setManageModal] = useState({ open: false, key: null, form: {} });
  const [predictModal, setPredictModal] = useState({ open: false, keys: [], file: '', efficiency: '', reliability: '', safety: '' });
  const [scrapModal, setScrapModal] = useState({ open: false, keys: [], date: null, dest: '' });

  const inRows = useMemo(() => inuse.filter((a) => (
    (!inApplied.code || String(a.code || '').includes(inApplied.code.trim()))
    && (!inApplied.name || String(a.name || '').includes(inApplied.name.trim()))
    && (!inApplied.station || String(a.station || '').includes(inApplied.station.trim()))
    && (!inApplied.ipc || String(a.ipc || '').includes(inApplied.ipc.trim()))
    && (!inApplied.vendor || String(a.vendor || '').includes(inApplied.vendor.trim()))
    && (!inApplied.cabinet || a.cabinet === inApplied.cabinet)
    && (!inApplied.predict || a.predictCfg === inApplied.predict)
  )), [inuse, inApplied]);

  const scRows = useMemo(() => scrapped.filter((a) => (
    (!scApplied.code || String(a.code || '').includes(scApplied.code.trim()))
    && (!scApplied.name || String(a.name || '').includes(scApplied.name.trim()))
    && (!scApplied.station || String(a.station || '').includes(scApplied.station.trim()))
    && (!scApplied.ipc || String(a.ipc || '').includes(scApplied.ipc.trim()))
    && (!scApplied.vendor || String(a.vendor || '').includes(scApplied.vendor.trim()))
    && (!scApplied.operator || String(a.operator || '').includes(scApplied.operator.trim()))
  )), [scrapped, scApplied]);

  const doInQuery = () => { setInApplied({ ...inF }); message.success('已按条件查询在用资产'); };
  const doInReset = () => { setInF({ ...EMPTY_IN_FILTER }); setInApplied({ ...EMPTY_IN_FILTER }); message.success('查询条件已重置'); };
  const doScQuery = () => { setScApplied({ ...scF }); message.success('已按条件查询报废资产'); };
  const doScReset = () => { setScF({ ...EMPTY_SC_FILTER }); setScApplied({ ...EMPTY_SC_FILTER }); message.success('查询条件已重置'); };

  // ---------- 新增 / 编辑资产 ----------
  const openAdd = () => setAssetModal({ open: true, editing: null, form: { ...EMPTY_ASSET } });
  const openEdit = (r) => setAssetModal({
    open: true, editing: r.key,
    form: {
      code: r.code, name: r.name, vendor: r.vendor, model: r.model,
      factory: r.factory && r.factory !== '—' ? dayjs(r.factory, 'YYYY-MM-DD') : null,
      power: r.power, capacity: r.capacity, ipc: r.ipc && r.ipc !== '—' ? r.ipc : '',
    },
  });
  const setAssetField = (k, v) => setAssetModal((s) => ({ ...s, form: { ...s.form, [k]: v } }));
  const submitAsset = () => {
    const f = assetModal.form;
    const required = [['code', '资产编号'], ['name', '名称'], ['vendor', '厂商'], ['model', '型号'], ['factory', '出厂日期'], ['power', '功率'], ['capacity', '容量']];
    for (const [k, label] of required) {
      if (!String(f[k] || '').trim()) { message.warning(`请输入${label}`); return; }
    }
    const factory = dayjs(f.factory).format('YYYY-MM-DD');
    if (assetModal.editing == null) {
      // 资产编号为唯一标识；已报废的编号不可再被新增（doc01 3.8 报废）
      if (inuse.some((a) => a.code === f.code.trim())) { message.warning('资产编号为唯一标识，不可重复'); return; }
      if (scrapped.some((a) => a.code === f.code.trim())) { message.warning('该资产编号已报废，不可再被新增'); return; }
      const row = {
        key: `a-${Date.now()}`, code: f.code.trim(), name: f.name.trim(),
        vendor: f.vendor.trim(), model: f.model.trim(), factory, power: f.power.trim(),
        capacity: f.capacity.trim(), ipc: f.ipc.trim() || '—', cabinet: '—',
        station: '—', operator: currentUser(), state: '在役', predictCfg: '未配置',
        customer: '—', device: '—', installDate: '—', installPlace: '—', history: [{ time: nowStr(), action: '新增资产' }],
      };
      setInuse((list) => [row, ...list]);
      message.success(`资产「${row.code}」已新增（默认为在用状态）`);
    } else {
      const old = inuse.find((a) => a.key === assetModal.editing) || {};
      setInuse((list) => list.map((a) => (a.key === assetModal.editing ? {
        ...a, name: f.name.trim(), vendor: f.vendor.trim(), model: f.model.trim(), factory,
        ipc: f.ipc.trim() || '—',
        history: [...(old.history || []), { time: nowStr(), action: '编辑资产信息' }],
      } : a)));
      message.success(`资产「${old.code}」已更新（资产编号 / 容量 / 功率不可变更）`);
    }
    setAssetModal({ open: false, editing: null, form: EMPTY_ASSET });
  };

  // ---------- 管理（二级页面） ----------
  const openManage = (r) => setManageModal({
    open: true, key: r.key,
    form: {
      customer: r.customer && r.customer !== '—' ? r.customer : undefined,
      station: r.station && r.station !== '—' ? r.station : undefined,
      device: r.device && r.device !== '—' ? r.device : undefined,
      cabinet: r.cabinet && r.cabinet !== '—' ? r.cabinet : undefined,
      installDate: r.installDate && r.installDate !== '—' ? r.installDate : '',
      installPlace: r.installPlace && r.installPlace !== '—' ? r.installPlace : '',
    },
  });
  const saveManage = () => {
    const { key, form } = manageModal;
    setInuse((list) => list.map((a) => (a.key === key ? {
      ...a, customer: form.customer || '—', station: form.station || '—', device: form.device || '—',
      cabinet: form.cabinet || '—', installDate: form.installDate || '—', installPlace: form.installPlace || '—',
      history: [...(a.history || []), { time: nowStr(), action: '关联客户 / 站点 / 站内设备' }],
    } : a)));
    message.success('资产关联信息已保存');
    setManageModal({ open: false, key: null, form: {} });
  };

  // ---------- 预测值配置（doc01 3.8：下载模板 → 导入第 1-120 月寿命曲线；非标准文件上传失败）----------
  const openPredict = (rows) => {
    if (!rows.length) return;
    setPredictModal({ open: true, keys: rows.map((r) => r.key), file: '', efficiency: '85.99', reliability: '233', safety: '233' });
  };
  const downloadTemplate = () => message.success('已下载资产寿命预测值模板（第 1-120 月）');
  const onPredictFile = (file) => {
    if (!/\.xlsx$/i.test(file.name)) {
      message.error('导入失败：非标准文件，请使用模板导出的 xlsx 文件');
      return Upload.LIST_IGNORE;
    }
    setPredictModal((s) => ({ ...s, file: file.name }));
    message.success('已导入第 1-120 月资产寿命预测曲线');
    return false;
  };
  const savePredict = () => {
    const { efficiency, reliability, safety } = predictModal;
    if (!String(efficiency).trim() || !String(reliability).trim() || !String(safety).trim()) {
      message.warning('系统效率、可靠性指标、安全性指标均为必填');
      return;
    }
    setInuse((list) => list.map((a) => (predictModal.keys.includes(a.key)
      ? { ...a, predictCfg: '已配置', history: [...(a.history || []), { time: nowStr(), action: '配置预测值曲线与指标' }] }
      : a)));
    message.success(`已为 ${predictModal.keys.length} 项资产配置预测值（系统效率 ${predictModal.efficiency}% / 可靠性 ${predictModal.reliability} / 安全性 ${predictModal.safety}）`);
    setSelected([]);
    setPredictModal((s) => ({ ...s, open: false }));
  };

  // ---------- 报废 ----------
  const openScrap = (rows) => {
    if (!rows.length) return;
    setScrapModal({ open: true, keys: rows.map((r) => r.key), date: null, dest: '' });
  };
  const saveScrap = () => {
    const { keys, date, dest } = scrapModal;
    if (!date) { message.warning('请选择报废日期'); return; }
    if (!dest.trim()) { message.warning('请输入资产去向'); return; }
    const moved = inuse.filter((a) => keys.includes(a.key)).map((a) => ({
      key: a.key, code: a.code, name: a.name, station: a.station, vendor: a.vendor, model: a.model,
      factory: a.factory, power: a.power, capacity: a.capacity, operator: a.operator,
      scrapAt: date.format('YYYY-MM-DD'), dest: dest.trim(),
    }));
    setInuse((list) => list.filter((a) => !keys.includes(a.key)));
    setScrapped((list) => [...moved, ...list]);
    setSelected([]);
    message.success(`已将 ${moved.length} 项资产移入报废资产，其编号不可再被新增`);
    setScrapModal({ open: false, keys: [], date: null, dest: '' });
  };
  const minFactory = useMemo(() => {
    const ds = inuse.filter((a) => scrapModal.keys.includes(a.key)).map((a) => a.factory).filter(Boolean);
    return ds.length ? ds.sort()[0] : '2000-01-01';
  }, [inuse, scrapModal.keys]);

  const deleteIn = (r) => { setInuse((list) => list.filter((a) => a.key !== r.key)); setSelected((s) => s.filter((k) => k !== r.key)); message.success(`资产「${r.code}」已删除`); };
  const deleteSc = (r) => { setScrapped((list) => list.filter((a) => a.key !== r.key)); message.success(`报废资产「${r.code}」已删除`); };

  // 列宽压缩：13 列（含操作）控制在内容区宽度内，避免横向滚动导致末尾列被固定操作列遮挡
  const inColumns = [
    { title: '资产编号', dataIndex: 'code', width: 105, render: (v) => <span style={{ color: '#00d4ff' }}>{v}</span>, fixed: 'left' },
    { title: '名称', dataIndex: 'name', width: 92, render: (v) => <span style={{ color: '#fff', fontWeight: 500 }}>{v}</span> },
    { title: '是否配置预测值', dataIndex: 'predictCfg', width: 95, render: (v) => <Tag color={v === '已配置' ? 'green' : 'default'} style={{ margin: 0 }}>{v}</Tag> },
    { title: '关联站点', dataIndex: 'station', width: 90 },
    { title: '储能柜类型', dataIndex: 'cabinet', width: 85 },
    { title: '工控机编号', dataIndex: 'ipc', width: 85 },
    { title: '厂商', dataIndex: 'vendor', width: 80 },
    { title: '型号', dataIndex: 'model', width: 90 },
    { title: '出厂日期', dataIndex: 'factory', width: 90 },
    { title: '功率', dataIndex: 'power', width: 70 },
    { title: '容量', dataIndex: 'capacity', width: 70 },
    { title: '操作人', dataIndex: 'operator', width: 70 },
    {
      title: '操作', key: 'op', fixed: 'right', width: 215,
      render: (_, r) => (
        <Space size={2} wrap>
          <Button type="link" size="small" onClick={() => openEdit(r)}>编辑</Button>
          <Button type="link" size="small" onClick={() => openManage(r)}>管理</Button>
          <Button type="link" size="small" onClick={() => openPredict([r])}>预测值配置</Button>
          <Popconfirm title={`确认删除资产「${r.code}」？`} onConfirm={() => deleteIn(r)} okText="确定" cancelText="取消">
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
          <Button type="link" size="small" danger onClick={() => openScrap([r])}>报废</Button>
        </Space>
      ),
    },
  ];

  // 列宽压缩：12 列（含操作）控制在内容区宽度内
  const scColumns = [
    { title: '资产编号', dataIndex: 'code', width: 110, render: (v) => <span style={{ color: '#00d4ff' }}>{v}</span> },
    { title: '名称', dataIndex: 'name', width: 95, render: (v) => <span style={{ color: '#fff', fontWeight: 500 }}>{v}</span> },
    { title: '最近关联站点', dataIndex: 'station', width: 105 },
    { title: '厂商', dataIndex: 'vendor', width: 85 },
    { title: '型号', dataIndex: 'model', width: 95 },
    { title: '出厂日期', dataIndex: 'factory', width: 95 },
    { title: '功率', dataIndex: 'power', width: 75 },
    { title: '容量', dataIndex: 'capacity', width: 75 },
    { title: '操作人', dataIndex: 'operator', width: 75 },
    { title: '报废日期', dataIndex: 'scrapAt', width: 95 },
    { title: '资产去向', dataIndex: 'dest', width: 110 },
    {
      title: '操作', key: 'op', fixed: 'right', width: 90,
      render: (_, r) => (
        <Popconfirm title={`确认删除报废资产「${r.code}」？`} onConfirm={() => deleteSc(r)} okText="确定" cancelText="取消">
          <Button type="link" size="small" danger>删除</Button>
        </Popconfirm>
      ),
    },
  ];

  const inputItem = (label, key, state, setState) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={labelStyle}>{label}</span>
      <Input size="small" value={state[key]} onChange={(e) => setState((s) => ({ ...s, [key]: e.target.value }))} placeholder={`请输入${label}`} prefix={<SearchOutlined />} style={{ width: 170 }} allowClear />
    </span>
  );

  const renderInuse = () => (
    <>
      {/* 缺陷 122：筛选移入子 tab、表格上方 */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 10 }}>
        {inputItem('资产编号', 'code', inF, setInF)}
        {inputItem('名称', 'name', inF, setInF)}
        {inputItem('关联站点', 'station', inF, setInF)}
        {inputItem('工控机编号', 'ipc', inF, setInF)}
        {inputItem('厂商', 'vendor', inF, setInF)}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={labelStyle}>储能柜类型</span>
          <Select size="small" value={inF.cabinet || ''} onChange={(v) => setInF((s) => ({ ...s, cabinet: v || undefined }))} style={{ width: 130 }}
            options={[{ value: '', label: '全部' }, ...CABINET_OPTIONS]} />
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={labelStyle}>是否配置预测值</span>
          <Select size="small" value={inF.predict || ''} onChange={(v) => setInF((s) => ({ ...s, predict: v || undefined }))} style={{ width: 120 }}
            options={[{ value: '', label: '全部' }, ...PREDICT_OPTIONS]} />
        </span>
        <Space>
          <Button type="primary" size="small" onClick={doInQuery}>查询</Button>
          <Button size="small" onClick={doInReset}>重置</Button>
        </Space>
      </div>

      <div style={{ marginBottom: 12 }}>
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAdd}>新增资产</Button>
      </div>

      <Table
        rowKey="key"
        dataSource={inRows}
        columns={inColumns}
        rowSelection={{ selectedRowKeys: selected, onChange: setSelected, preserveSelectedRowKeys: true }}
        pagination={{ pageSize: 10, size: 'small' }}
        size="middle"
        scroll={{ x: 1240 }}
      />

      {/* doc01 3.8 交互：勾选列表前复选框 → 左下角展示共选择资产个数，可批量配置预测值，支持翻页勾选 */}
      <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={labelStyle}>已选 <b style={{ color: '#00d4ff' }}>{selected.length}</b> 项</span>
        <Button size="small" type="primary" disabled={!selected.length} onClick={() => openPredict(inuse.filter((a) => selected.includes(a.key)))}>预测值配置</Button>
        <Button size="small" disabled={!selected.length} onClick={() => setSelected([])}>取消</Button>
      </div>
    </>
  );

  const renderScrapped = () => (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 10 }}>
        {inputItem('资产编号', 'code', scF, setScF)}
        {inputItem('名称', 'name', scF, setScF)}
        {inputItem('关联站点', 'station', scF, setScF)}
        {inputItem('工控机编号', 'ipc', scF, setScF)}
        {inputItem('厂商', 'vendor', scF, setScF)}
        {inputItem('操作人', 'operator', scF, setScF)}
        <Space>
          <Button type="primary" size="small" onClick={doScQuery}>查询</Button>
          <Button size="small" onClick={doScReset}>重置</Button>
        </Space>
      </div>
      <Table rowKey="key" dataSource={scRows} columns={scColumns} pagination={{ pageSize: 10, size: 'small' }} size="middle" scroll={{ x: 1110 }} />
    </>
  );

  const af = assetModal.form;
  const mf = manageModal.form;
  const manageRow = inuse.find((a) => a.key === manageModal.key) || {};

  return (
    <PageSkeleton
      title={meta ? pick(meta.label, lang) : '资产管理'}
      desc={meta ? pick(meta.desc, lang) : ''}
      crumb={meta ? [pick(meta.groupLabel, lang), pick(meta.sectionLabel, lang), pick(meta.label, lang)] : []}
      filters={[]}
      actions={[]}
    >
      <div className="panel blk-tabs">
        <ScrollTabs activeKey={tab} onChange={setTab} items={[
          { key: 'inuse', label: `在用资产 (${inuse.length})`, children: renderInuse() },
          { key: 'scrapped', label: `报废资产 (${scrapped.length})`, children: renderScrapped() },
        ]} />
      </div>

      {/* 新增 / 编辑资产 */}
      <Modal
        title={assetModal.editing != null ? '编辑资产' : '新增资产'}
        open={assetModal.open} onOk={submitAsset}
        onCancel={() => setAssetModal({ open: false, editing: null, form: EMPTY_ASSET })}
        okText="确定" cancelText="取消" destroyOnHidden width={680}
      >
        <div style={{ marginBottom: 10, color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>
          * 为必填项；资产编号作为唯一标识，功率 / 容量影响计算结果，故新增后不可变更。
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div><div style={labelStyle}>资产编号 *</div><Input value={af.code} disabled={assetModal.editing != null} onChange={(e) => setAssetField('code', e.target.value)} placeholder="请输入资产编号" /></div>
          <div><div style={labelStyle}>名称 *</div><Input value={af.name} onChange={(e) => setAssetField('name', e.target.value)} placeholder="请输入名称" /></div>
          <div><div style={labelStyle}>厂商 *</div><Input value={af.vendor} onChange={(e) => setAssetField('vendor', e.target.value)} placeholder="请输入厂商" /></div>
          <div><div style={labelStyle}>型号 *</div><Input value={af.model} onChange={(e) => setAssetField('model', e.target.value)} placeholder="请输入型号" /></div>
          <div><div style={labelStyle}>出厂日期 *</div><DatePicker style={{ width: '100%' }} value={af.factory} onChange={(d) => setAssetField('factory', d)} placeholder="请选择出厂日期" /></div>
          <div><div style={labelStyle}>功率/kW *</div><Input value={af.power} disabled={assetModal.editing != null} onChange={(e) => setAssetField('power', e.target.value)} placeholder="请输入功率" /></div>
          <div><div style={labelStyle}>容量/kWh *</div><Input value={af.capacity} disabled={assetModal.editing != null} onChange={(e) => setAssetField('capacity', e.target.value)} placeholder="请输入容量" /></div>
          <div><div style={labelStyle}>工控机编号</div><Input value={af.ipc} onChange={(e) => setAssetField('ipc', e.target.value)} placeholder="填写储能柜内工控机编号（非必填）" /></div>
        </div>
      </Modal>

      {/* 管理（二级页面） */}
      <Modal
        title={`资产关联管理 · ${manageRow.code || ''}`}
        open={manageModal.open} onOk={saveManage}
        onCancel={() => setManageModal({ open: false, key: null, form: {} })}
        okText="保存" cancelText="取消" destroyOnHidden width={620}
      >
        <div style={{ marginBottom: 10, color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>关联客户 / 站点 / 站内设备三者联动，并可设置储能柜类型与安装信息。</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div><div style={labelStyle}>关联客户</div><Select style={{ width: '100%' }} value={mf.customer} onChange={(v) => setManageModal((s) => ({ ...s, form: { ...s.form, customer: v } }))} placeholder="请选择客户" allowClear options={CUSTOMER_OPTIONS} /></div>
          <div><div style={labelStyle}>关联站点</div><Select style={{ width: '100%' }} value={mf.station} onChange={(v) => setManageModal((s) => ({ ...s, form: { ...s.form, station: v, device: undefined } }))} placeholder="请选择站点" allowClear options={STATION_OPTIONS} /></div>
          <div><div style={labelStyle}>站内设备</div><Select style={{ width: '100%' }} value={mf.device} onChange={(v) => setManageModal((s) => ({ ...s, form: { ...s.form, device: v } }))} placeholder="请先选择站点" allowClear options={DEVICE_OPTIONS} /></div>
          <div><div style={labelStyle}>储能柜类型</div><Select style={{ width: '100%' }} value={mf.cabinet} onChange={(v) => setManageModal((s) => ({ ...s, form: { ...s.form, cabinet: v } }))} placeholder="请选择" allowClear options={CABINET_OPTIONS} /></div>
          <div><div style={labelStyle}>安装日期</div><Input value={mf.installDate} onChange={(e) => setManageModal((s) => ({ ...s, form: { ...s.form, installDate: e.target.value } }))} placeholder="YYYY-MM-DD" /></div>
          <div><div style={labelStyle}>安装地点</div><Input value={mf.installPlace} onChange={(e) => setManageModal((s) => ({ ...s, form: { ...s.form, installPlace: e.target.value } }))} placeholder="请输入安装地点" /></div>
        </div>
        <div style={{ marginTop: 16 }}>
          <div style={{ ...labelStyle, marginBottom: 6 }}>历史记录</div>
          {(manageRow.history || []).length ? (
            <div className="dev-kv">
              {(manageRow.history || []).map((h, i) => (
                <div className="dev-kv-row" key={i}><span className="dev-kv-k">{h.time}</span><span className="dev-kv-v">{h.action}</span></div>
              ))}
            </div>
          ) : <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>暂无历史记录</div>}
        </div>
      </Modal>

      {/* 预测值配置 */}
      <Modal
        title="预测值配置"
        open={predictModal.open} onOk={savePredict}
        onCancel={() => setPredictModal((s) => ({ ...s, open: false }))}
        okText="确定" cancelText="取消" destroyOnHidden width={560}
      >
        <div style={{ marginBottom: 12 }}>
          <div style={labelStyle}>资产寿命</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
            <span style={{ color: '#00d4ff', fontSize: 12 }}>{predictModal.file || '未导入预测曲线'}</span>
            <Upload accept=".xlsx" showUploadList={false} beforeUpload={onPredictFile}>
              <Button size="small" icon={<UploadOutlined />}>点击导入寿命预测曲线</Button>
            </Upload>
            <Button size="small" icon={<DownloadOutlined />} onClick={downloadTemplate}>下载模板</Button>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div><div style={labelStyle}>系统效率 *（%）</div><Input value={predictModal.efficiency} onChange={(e) => setPredictModal((s) => ({ ...s, efficiency: e.target.value }))} placeholder="如 85.99" /></div>
          <div><div style={labelStyle}>可靠性指标 *（每月告警/次）</div><Input value={predictModal.reliability} onChange={(e) => setPredictModal((s) => ({ ...s, reliability: e.target.value }))} placeholder="如 233" /></div>
          <div><div style={labelStyle}>安全性指标 *（每月告警/次）</div><Input value={predictModal.safety} onChange={(e) => setPredictModal((s) => ({ ...s, safety: e.target.value }))} placeholder="如 233" /></div>
        </div>
      </Modal>

      {/* 报废 */}
      <Modal
        title="资产报废"
        open={scrapModal.open} onOk={saveScrap}
        onCancel={() => setScrapModal({ open: false, keys: [], date: null, dest: '' })}
        okText="确认报废" cancelText="取消" destroyOnHidden
      >
        <div style={{ marginBottom: 12, color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>
          报废日期只可选择出厂日期（{minFactory}）至今日；确认后该资产移入报废资产 tab，且编号不可再被新增。共 {scrapModal.keys.length} 项。
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={labelStyle}>报废日期 *</div>
            <DatePicker
              style={{ width: '100%' }} value={scrapModal.date}
              onChange={(d) => setScrapModal((s) => ({ ...s, date: d }))}
              disabledDate={(d) => d.isBefore(dayjs(minFactory, 'YYYY-MM-DD'), 'day') || d.isAfter(dayjs(), 'day')}
            />
          </div>
          <div><div style={labelStyle}>资产去向 *</div><Input value={scrapModal.dest} onChange={(e) => setScrapModal((s) => ({ ...s, dest: e.target.value }))} placeholder="请输入资产去向，如 厂家回收 / 报废处置" /></div>
        </div>
      </Modal>
    </PageSkeleton>
  );
};

export default CustomerAssetPage;
